/**
 * ContractDetailPage — dettaglio di un singolo contratto/POD.
 *
 * Replica del mobile `ContractDetail.js` (versione semplificata).
 * Mostra: indirizzo + etichetta editabile, codice POD, potenza, offerta,
 * data inizio, metodo pagamento, intervallo fatturazione.
 *
 * L'etichetta del POD è modificabile solo sui contratti propri (non
 * sui contratti in monitoraggio).
 */

import { useState } from 'react'
import { AlertCircle, ChevronLeft, Eye, Home, Pencil } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { Card } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Skeleton } from '@/components/ui/skeleton'
import { useActiveContract } from '@/features/auth/hooks/useActiveContract'
import { PodLabelDialog } from './components/PodLabelDialog'

export function ContractDetailPage() {
  const { codice } = useParams<{ codice: string }>()
  const {
    cliente,
    contratti,
    monitoraggio,
    isLoading,
    error,
  } = useActiveContract()

  const all = [...contratti, ...monitoraggio]
  const contract = all.find((c) => c.codice_contratto === codice)
  const isMonitored = monitoraggio.some((c) => c.codice_contratto === codice)
  const canEditLabel = contract != null && !isMonitored

  const [labelDialogOpen, setLabelDialogOpen] = useState(false)

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4">
      <Link
        to="/profilo"
        className="inline-flex w-fit items-center gap-1 text-sm font-semibold text-secondary hover:underline"
      >
        <ChevronLeft className="h-4 w-4" />
        Profilo
      </Link>

      <header>
        <h1 className="text-2xl font-bold text-foreground md:text-3xl">
          Dettaglio fornitura
        </h1>
      </header>

      {error ? (
        <EmptyState
          icon={<AlertCircle className="h-5 w-5 text-destructive" />}
          title="Errore nel caricamento"
          description={error.message}
        />
      ) : isLoading ? (
        <DetailSkeleton />
      ) : !contract ? (
        <EmptyState
          title="Contratto non trovato"
          description="Questo contratto potrebbe non essere più associato al tuo account."
        />
      ) : (
        <Card className="flex flex-col gap-0 p-0">
          <div className="flex items-center gap-3 border-b border-border p-5">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary/15 text-secondary">
              {isMonitored ? <Eye className="h-5 w-5" /> : <Home className="h-5 w-5" />}
            </div>
            <div className="flex flex-1 flex-col min-w-0">
              <h2 className="truncate text-base font-bold text-foreground">
                {contract.pod.etichetta || contract.pod.indirizzo || 'Fornitura'}
              </h2>
              {contract.pod.etichetta && (
                <p className="truncate text-xs text-muted-foreground">
                  {contract.pod.indirizzo}
                </p>
              )}
              <p className="mt-1 font-mono text-[11px] text-muted-foreground">
                POD {contract.pod.pod}
              </p>
            </div>
            {canEditLabel && (
              <button
                type="button"
                onClick={() => setLabelDialogOpen(true)}
                aria-label="Modifica etichetta fornitura"
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Pencil className="h-4 w-4" />
              </button>
            )}
          </div>

          <dl className="grid grid-cols-1 gap-0 sm:grid-cols-2">
            <DetailItem label="Codice contratto" value={contract.codice_contratto} />
            <DetailItem label="Stato" value={<StatoBadge raw={contract.stato} />} />
            <DetailItem label="Offerta" value={contract.offerta?.nome ?? '—'} />
            <DetailItem label="Tariffa" value={contract.offerta?.tariffa ?? '—'} />
            <DetailItem
              label="Potenza disponibile"
              value={
                contract.potenza_disponibile ? `${contract.potenza_disponibile} kW` : '—'
              }
            />
            <DetailItem
              label="Potenza impegnata"
              value={contract.potenza_impegnata ? `${contract.potenza_impegnata} kW` : '—'}
            />
            <DetailItem
              label="Intervallo fatturazione"
              value={contract.intervallo_fatturazione ?? '—'}
            />
            <DetailItem
              label="Metodo di pagamento"
              value={contract.metodo_pagamento ?? '—'}
            />
            <DetailItem label="Data inizio" value={contract.data_inizio ?? '—'} />
            <DetailItem label="Uso" value={contract.uso ?? '—'} />
          </dl>
        </Card>
      )}

      {canEditLabel && contract && cliente && (
        <PodLabelDialog
          open={labelDialogOpen}
          onOpenChange={setLabelDialogOpen}
          codiceCliente={cliente.codice_cliente}
          codicePod={contract.pod.pod}
          currentLabel={contract.pod.etichetta}
        />
      )}
    </div>
  )
}

/**
 * Badge per lo stato del contratto.
 * Backend usa boolean / "true" / "false"; mappiamo a 3 stati visivi:
 *  - Attivo   → success (verde)
 *  - Non attivo → muted (grigio)
 *  - Altro / sconosciuto → outline neutro col valore raw capitalizzato
 */
function StatoBadge({ raw }: { raw: string | boolean | null | undefined }) {
  const isActive = raw === true || raw === 'true' || raw === 'True'
  const isInactive = raw === false || raw === 'false' || raw === 'False'

  if (!isActive && !isInactive && !raw) {
    return <span className="text-sm text-muted-foreground">—</span>
  }

  if (isActive) {
    return (
      <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-success/15 px-2.5 py-0.5 text-xs font-semibold text-success">
        <span className="h-1.5 w-1.5 rounded-full bg-success" aria-hidden />
        Attivo
      </span>
    )
  }

  if (isInactive) {
    return (
      <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-muted px-2.5 py-0.5 text-xs font-semibold text-muted-foreground">
        <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/70" aria-hidden />
        Non attivo
      </span>
    )
  }

  // Fallback: altra stringa (es. "Sospeso") → capitalize
  const s = String(raw)
  const display = s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
  return (
    <span className="inline-flex w-fit items-center rounded-full border border-border bg-card px-2.5 py-0.5 text-xs font-medium text-foreground">
      {display}
    </span>
  )
}

function DetailItem({
  label,
  value,
}: {
  label: string
  value: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-1 border-b border-border p-5 last:border-b-0 sm:[&:nth-last-child(2)]:border-b-0">
      <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium text-foreground">{value}</dd>
    </div>
  )
}

function DetailSkeleton() {
  return (
    <Card className="flex flex-col gap-0 p-0">
      <div className="flex items-center gap-3 border-b border-border p-5">
        <Skeleton className="h-12 w-12 rounded-xl" />
        <div className="flex flex-col gap-2">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-3 w-56" />
          <Skeleton className="h-3 w-32" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex flex-col gap-2 border-b border-border p-5 last:border-b-0">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-4 w-32" />
          </div>
        ))}
      </div>
    </Card>
  )
}
